import { useLayoutEffect, useState } from 'react';
import { getRemoteControlVideoElement } from '../../functions';
/**
 * Tracks where the shared screen is actually drawn on this page.
 *
 * The video is letterboxed or cropped inside its container depending on the
 * layout and the shape of the shared screen, and Jitsi resizes it imperatively
 * rather than through React. Measuring it is the only reliable way to put an
 * overlay exactly over the picture — and the same rectangle is what turns a
 * pointer position into a position on the remote desktop.
 *
 * @returns {IVideoRect|undefined}
 */
export function useVideoRect() {
    const [rect, setRect] = useState();
    useLayoutEffect(() => {
        const container = document.getElementById('videospace');
        const measure = () => {
            const video = getRemoteControlVideoElement();
            if (!video || !container) {
                setRect(undefined);
                return;
            }
            const videoBox = video.getBoundingClientRect();
            const containerBox = container.getBoundingClientRect();
            if (!videoBox.width || !videoBox.height) {
                setRect(undefined);
                return;
            }
            const next = {
                height: videoBox.height,
                left: videoBox.left - containerBox.left,
                top: videoBox.top - containerBox.top,
                width: videoBox.width
            };
            setRect(current => {
                if (current
                    && current.left === next.left
                    && current.top === next.top
                    && current.width === next.width
                    && current.height === next.height) {
                    return current;
                }
                return next;
            });
        };
        measure();
        const observer = new ResizeObserver(measure);
        const video = getRemoteControlVideoElement();
        video && observer.observe(video);
        container && observer.observe(container);
        window.addEventListener('resize', measure);
        // The video element is replaced when the participant on stage changes,
        // and its position moves when the filmstrip opens or closes — neither of
        // which the observers above see. A slow poll costs nothing and keeps the
        // overlay honest through all of it.
        const poll = window.setInterval(measure, 500);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measure);
            window.clearInterval(poll);
        };
    }, []);
    return rect;
}
